import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProject, updateProject } from '../utils/api';

const fmtMoney = (n) => n != null ? `$${Number(n).toFixed(2)}` : null;

const loadCache = (id) => {
  try { return JSON.parse(sessionStorage.getItem(`rescope-chat-${id}`)) || null; } catch { return null; }
};

const ProjectRescope = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [done, setDone] = useState(false);
  const [updatedProject, setUpdatedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    getProject(id)
      .then(({ data }) => {
        setExisting(data);
        const cached = loadCache(id);
        if (cached?.messages?.length > 0) {
          setMessages(cached.messages);
          setDone(cached.done ?? false);
          setUpdatedProject(cached.updatedProject ?? null);
        } else {
          setMessages([{
            role: 'assistant',
            content: `Your "${data.title}" plan is loaded — I can see the full scope including ${data.materials?.length || 0} materials, ${data.tools?.length || 0} tools, and ${data.steps?.length || 0} steps. What changed? Tell me what new research you found or which direction you want to go instead.`,
          }]);
        }
      })
      .catch(() => setLoadError('Could not load project.'));
  }, [id]);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(`rescope-chat-${id}`, JSON.stringify({ messages, done, updatedProject }));
    }
  }, [messages, done, updatedProject, id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  useEffect(() => {
    if (!thinking) inputRef.current?.focus();
  }, [thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setThinking(true);
    setDone(false);
    setUpdatedProject(null);

    // API messages exclude the static welcome
    const apiMessages = newMessages.filter((m, i) => !(i === 0 && m.role === 'assistant'));

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const response = await fetch('/api/intake/rescope', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: id, messages: apiMessages }),
      });

      if (!response.ok) {
        let msg = 'Request failed';
        try { msg = (await response.json()).message || msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedText = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(part.slice(6)); } catch { continue; }

          if (evt.type === 'text') {
            streamedText += evt.text;
            const jsonIdx = streamedText.indexOf('```json');
            const displayText = jsonIdx >= 0
              ? (streamedText.slice(0, jsonIdx).trimEnd() || '') + '\n\n_Generating updated plan…_'
              : streamedText;
            setMessages((prev) => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: displayText.trim() },
            ]);
          } else if (evt.type === 'done') {
            setThinking(false);
            if (evt.done && evt.project) {
              setDone(true);
              setUpdatedProject(evt.project);
              setMessages((prev) => [
                ...prev.slice(0, -1),
                { role: 'assistant', content: evt.reply || streamedText },
              ]);
            }
          } else if (evt.type === 'error') {
            throw new Error(evt.message);
          }
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: `Sorry, ${err.message || 'something went wrong'}. Please try again.` },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleSave = async () => {
    if (!updatedProject) return;
    setSaving(true);
    try {
      await updateProject(id, updatedProject);
      sessionStorage.removeItem(`rescope-chat-${id}`);
      navigate(`/projects/${id}`);
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;
  if (!existing) return <div className="loading">Loading…</div>;

  const p = updatedProject;
  const materialTotal = p?.materials?.reduce((s, m) => s + (m.estimatedCost || 0), 0) ?? 0;
  const toolsNeeded = p?.tools?.filter((t) => !t.owned) ?? [];
  const toolsTotal = toolsNeeded.reduce((s, t) => s + (t.estimatedCost || 0), 0);
  const grandTotal = materialTotal + toolsTotal;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Rescope Project</h1>
          <p className="page-subtitle">
            <Link to={`/projects/${id}`} style={{ color: 'var(--muted)', textDecoration: 'none' }}>
              ← {existing.title}
            </Link>
          </p>
        </div>
      </div>

      <div className="intake-layout">
        {/* Chat */}
        <div className="intake-chat">
          <div className="intake-messages">
            {messages.map((m, i) => (
              <div key={i} className={`intake-msg intake-msg-${m.role}`}>
                {m.role === 'assistant' && <span className="intake-avatar">🤖</span>}
                <div className="intake-bubble">{m.content}</div>
              </div>
            ))}
            {thinking && messages[messages.length - 1]?.content === '' && (
              <div className="intake-msg intake-msg-assistant">
                <span className="intake-avatar">🤖</span>
                <div className="intake-bubble intake-thinking"><span /><span /><span /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="intake-input-row">
            <textarea
              ref={inputRef}
              className="intake-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={done ? 'Refine the plan further… (Enter to send)' : "Describe what changed or what direction you're going… (Enter to send)"}
              rows={2}
              disabled={thinking}
            />
            <button className="btn btn-primary intake-send" onClick={send} disabled={!input.trim() || thinking}>
              Send
            </button>
          </div>
        </div>

        {/* Updated plan summary */}
        {done && p && (
          <div className="intake-summary">
            <div className="intake-summary-title">
              <h2>{p.title}</h2>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                <span className={`badge badge-priority-${p.priority}`}>{p.priority}</span>
                <span className="badge">{p.category}</span>
                {p.estimatedDuration && <span className="badge">{p.estimatedDuration}</span>}
              </div>
            </div>

            {p.description && <p className="intake-summary-desc">{p.description}</p>}

            {p.materials?.length > 0 && (
              <div className="intake-plan-section">
                <h3>🛒 Updated Shopping List</h3>
                <table className="intake-plan-table">
                  <thead><tr><th>Item</th><th>Qty</th><th>Est. Cost</th></tr></thead>
                  <tbody>
                    {p.materials.map((m, i) => (
                      <tr key={i}>
                        <td>
                          <span className="intake-item-name">{m.name}</span>
                          {m.notes && <span className="intake-item-note">{m.notes}</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{m.quantity || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtMoney(m.estimatedCost) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  {materialTotal > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>Materials subtotal</td>
                        <td style={{ fontWeight: 700 }}>{fmtMoney(materialTotal)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {p.tools?.length > 0 && (
              <div className="intake-plan-section">
                <h3>🔧 Tools</h3>
                <ul className="intake-tools-list">
                  {p.tools.map((t, i) => (
                    <li key={i} className={`intake-tool-item${t.owned ? ' owned' : ' needed'}`}>
                      <span className="intake-tool-status">{t.owned ? '✓' : '+'}</span>
                      <span className="intake-tool-name">{t.name}</span>
                      {!t.owned && t.estimatedCost != null && <span className="intake-tool-cost">{fmtMoney(t.estimatedCost)}</span>}
                      {t.owned && <span className="intake-tool-own-label">you have this</span>}
                    </li>
                  ))}
                </ul>
                {toolsTotal > 0 && <p className="intake-subtotal">Tools to buy: {fmtMoney(toolsTotal)}</p>}
              </div>
            )}

            {grandTotal > 0 && (
              <div className="intake-cost-total">
                <span>Estimated Total</span>
                <span>{fmtMoney(grandTotal)}</span>
              </div>
            )}

            {p.steps?.length > 0 && (
              <div className="intake-plan-section">
                <h3>📋 Updated Steps</h3>
                <ol className="intake-steps-list">
                  {p.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            )}

            {p.diagram && (
              <div className="intake-plan-section">
                <h3>📐 Layout</h3>
                <pre className="intake-diagram">{p.diagram}</pre>
              </div>
            )}

            {p.notes && (
              <div className="intake-plan-section">
                <h3>⚠ Notes</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>{p.notes}</p>
              </div>
            )}

            <div className="intake-summary-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : '✓ Update Project'}
              </button>
              <button className="btn btn-secondary" onClick={() => { sessionStorage.removeItem(`rescope-chat-${id}`); navigate(`/projects/${id}`); }}>
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectRescope;
